import {
  GetUsersResponse,
  OrganizationUnit,
  OrganizationUnitNameAndId,
  OrganizationUnitType,
  UserType,
} from '@inplay/models';
import {
  FieldConfig,
  UserPresentationStrategy,
} from '../userPresentationStrategy.interface';
import i18n from '../../../i18n';
import { userTypeOptions } from '../../../component/userModal/helper';
import { isUndefined } from 'lodash';
import { Option } from '../../../component/dropDownInput/dropDownInput.type';
import {
  filterAndConvertOrgUnitsToOption,
  getChildOrganizationUnitOptions,
  hasOrganizationUnit,
} from '../../../utils/helper';
import { getFullName } from '../../../pages/userManagement/userManagementHelper';
import moment from 'moment';

export class VenueStaffPresentationStrategy
  implements UserPresentationStrategy
{
  private translate: (key: string) => string;
  private userDetail: GetUsersResponse;

  constructor(userDetail: GetUsersResponse) {
    /**
     * i18next's type definitions don't properly handle the return type of i18n.t()
     * function can return string | object | array, but we know in our usage it will always return string
     * This is a known TypeScript limitation with i18next - see: https://github.com/i18next/i18next/issues/1849
     */
    //@ts-ignore
    this.translate = (key: string) => i18n.t(key) || key;
    this.userDetail = userDetail;
  }

  public getViewableFieldsOfUser(
    subject: GetUsersResponse,
  ): Record<string, any> {
    // OrganizationAdmin can see all fields in a formatted way
    switch (subject.type) {
      case UserType.VENUE_STAFF:
        return {
          [this.translate('userManagement.userType')]: subject.type,
          [this.translate('userManagement.name')]:
            subject.firstName + ' ' + subject.lastName,
          [this.translate('userManagement.subOrganizations')]:
            subject.subOrganizations && subject.subOrganizations.length > 0
              ? subject.subOrganizations
                  .map(
                    (subOrganization: OrganizationUnitNameAndId) =>
                      subOrganization.name,
                  )
                  .join(', ')
              : '',
          [this.translate('userManagement.photo')]: subject?.profilePhoto,
          [this.translate('userManagement.venue')]: subject.venue?.name,
          [this.translate('userManagement.venueBranches')]:
            subject.venueBranchs && subject.venueBranchs.length > 0
              ? subject.venueBranchs
                  .map(
                    (venueBranch: OrganizationUnitNameAndId) =>
                      venueBranch.name,
                  )
                  .join(', ')
              : '',
          [this.translate('userManagement.addedBy')]:
            getFullName(
              subject.addedBy?.firstName ?? '',
              subject.addedBy?.lastName ?? '',
            ) +
            ', ' +
            moment(subject.addedBy?.date).format('DD/MM/YYYY'),
        };
      default:
        const error: string = `Incorrect user type or user not allowed to access user type`;
        throw error;
    }
  }

  public getEditableFieldsOfUser(
    user: GetUsersResponse,
    organizationUnits: (OrganizationUnit & { _id: string })[],
  ): FieldConfig[] {
    const commonFields: FieldConfig[] = [
      {
        id: 'userType',
        type: 'readonly',
        label: this.translate('userManagement.userTypeTitle'),
        required: true,
        defaultValue: user.type,
      },
      {
        id: 'firstName',
        type: 'text',
        label: this.translate('userManagement.firstName'),
        required: true,
        placeholder: this.translate('userManagement.firstName'),
        defaultValue: user.firstName,
      },
      {
        id: 'lastName',
        type: 'text',
        label: this.translate('userManagement.lastName'),
        required: true,
        placeholder: this.translate('userManagement.lastName'),
        defaultValue: user.lastName,
      },
      {
        id: 'photo',
        type: 'photo',
        label: this.translate('userManagement.photo'),
        required: false,
        defaultValue: user.profilePhoto,
      },
    ];
    // Fields specific to each user type
    switch (user.type) {
      case UserType.VENUE_STAFF:
        return [
          ...commonFields,
          {
            id: 'venue',
            type: 'readonly',
            label: this.translate('userManagement.venue'),
            required: true,
            placeholder: 'Select',
            options: filterAndConvertOrgUnitsToOption(
              organizationUnits,
              OrganizationUnitType.VENUE,
            ),
            defaultValue: user.venue?.id || '',
          },
          {
            id: 'venueBranch',
            type: 'multiselect',
            label: this.translate('userManagement.venueBranches'),
            required: true,
            options: getChildOrganizationUnitOptions(organizationUnits),
            placeholder: 'Select',
            defaultValue: user.venueBranchs
              ? user.venueBranchs.map(
                  (branch: OrganizationUnitNameAndId) => branch?.id,
                )
              : '',
          },
          {
            id: 'pin',
            type: 'pin',
            label: this.translate('userManagement.pin'),
            required: true,
            options: [
              {
                label: this.translate('userManagement.custom'),
                value: 'custom',
              },
              {
                label: this.translate('userManagement.autoGenerate'),
                value: 'default',
              },
            ],
            defaultValue: {
              option: 'default',
              pin: Math.floor(1000 + Math.random() * 9000).toString(),
            },
            placeholder: this.translate('userManagement.enterPin'),
          },
        ];
      default:
        const error: string = `Incorrect user type or user not allowed to access user type`;
        throw error;
    }
  }

  public getCreationFieldsConfig(
    userType: UserType,
    organizationUnits: (OrganizationUnit & { _id: string })[],
  ): FieldConfig[] {
    if (isUndefined(userType)) {
      userType = UserType.VENUE_STAFF;
    }
    const allowedUserTypes: UserType[] = [
      UserType.VENUE_MANAGER,
      UserType.VENUE_STAFF,
    ];
    const allowedUserTypeOptions: Option[] = userTypeOptions.filter(
      (userTypeOption: Option) => {
        return allowedUserTypes.includes(userTypeOption.value as UserType);
      },
    );
    const commonFields: FieldConfig[] = [
      {
        id: 'userType',
        type: 'dropdown',
        label: this.translate('userManagement.userTypeTitle'),
        required: true,
        options: allowedUserTypeOptions,
        defaultValue: UserType.VENUE_STAFF,
      },
      {
        id: 'firstName',
        type: 'text',
        label: this.translate('userManagement.firstName'),
        required: true,
        placeholder: this.translate('userManagement.firstName'),
      },
      {
        id: 'lastName',
        type: 'text',
        label: this.translate('userManagement.lastName'),
        required: true,
        placeholder: this.translate('userManagement.lastName'),
      },
    ];
    // Fields specific to each user type
    switch (userType) {
      case UserType.VENUE_STAFF:
        return [
          ...commonFields,
          {
            id: 'venue',
            type: 'readonly',
            label: this.translate('userManagement.venue'),
            required: true,
            defaultValue: this.userDetail?.venue?.id ?? '',
            options: filterAndConvertOrgUnitsToOption(
              organizationUnits,
              OrganizationUnitType.VENUE,
            ),
            placeholder: 'Select',
          },
          {
            id: 'venueBranch',
            type: 'multiselect',
            label: this.translate('userManagement.venueBranches'),
            required: true,
            options: getChildOrganizationUnitOptions(
              organizationUnits.filter(
                (organizationUnit: OrganizationUnit & { _id: string }) =>
                  hasOrganizationUnit(
                    organizationUnit,
                    this.userDetail.venue!.id,
                  ),
              ),
            ),
            placeholder: 'Select',
          },
          {
            id: 'pin',
            type: 'pin',
            label: this.translate('userManagement.pin'),
            required: true,
            options: [
              {
                label: this.translate('userManagement.custom'),
                value: 'custom',
              },
              {
                label: this.translate('userManagement.autoGenerate'),
                value: 'default',
              },
            ],
            defaultValue: {
              option: 'default',
              pin: Math.floor(1000 + Math.random() * 9000).toString(),
            },
            placeholder: this.translate('userManagement.enterPin'),
          },
        ];
      default:
        const error: string = `Incorrect user type or user not allowed to access user type`;
        throw error;
    }
  }

  public getViewableFieldsForProfile(
    subject: GetUsersResponse,
  ): Record<string, string | number | undefined> {
    // OrganizationAdmin can see all fields in a formatted way
    return {
      [this.translate('onboarding.firstName')]: subject.firstName,
      [this.translate('onboarding.lastName')]: subject.lastName,
      [this.translate('userManagement.userType')]: subject.type,
      [this.translate('userManagement.venue')]: subject.venue?.name,
      [this.translate('userManagement.venueBranches')]:
        subject.venueBranchs && subject.venueBranchs.length > 0
          ? subject.venueBranchs
              .map((venueBranch: OrganizationUnitNameAndId) => venueBranch.name)
              .join(', ')
          : '',
    };
  }

  public getEditableFieldsForProfile(subject: GetUsersResponse): FieldConfig[] {
    return [
      {
        id: 'firstName',
        type: 'text',
        label: this.translate('userManagement.firstName'),
        required: true,
        placeholder: this.translate('userManagement.firstName'),
        defaultValue: subject.firstName,
      },
      {
        id: 'lastName',
        type: 'text',
        label: this.translate('userManagement.lastName'),
        required: true,
        placeholder: this.translate('userManagement.lastName'),
        defaultValue: subject.lastName,
      },
      {
        id: 'userType',
        type: 'readonly',
        label: this.translate('userManagement.userTypeTitle'),
        placeholder: this.translate('userManagement.userTypeTitle'),

        required: true,
        defaultValue: subject.type,
      },
      {
        id: 'venue',
        type: 'readonly',
        label: this.translate('userManagement.venue'),
        placeholder: this.translate('userManagement.venue'),
        required: true,
        defaultValue: subject.venue?.name,
      },
      {
        id: 'venueBranch',
        type: 'readonly',
        label: this.translate('userManagement.venueBranches'),
        placeholder: this.translate('userManagement.venueBranches'),
        required: true,
        defaultValue: subject.venueBranchs
          ? subject.venueBranchs
              .map((venueBranch: OrganizationUnitNameAndId) => venueBranch.name)
              .join(', ')
          : '',
      },
    ];
  }
}
